import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import Table from '@components/Table';
import { dateFormat } from '@utils/formatters';
import useSearchParams from '@hooks/useSearchParams';
import { DatePicker as AntdDatePicker, Input } from 'antd';
import useSwr from '@hooks/useSwr';

const { Search } = Input;

interface ITransactionsProps {
  imsi?: number | string | undefined;
}

const Transactions = ({ imsi }: ITransactionsProps) => {
  const { t } = useTranslation(['transactions']);

  const [dateValue, setDateValue] = useState<Moment | null>(moment());
  const [iccidValue, setIccidValue] = useState('');
  const [applicationValue, setApplicationValue] = useState('');
  const [messageTypeRequestValue, setMessageTypeRequestValue] = useState('');
  const [deviceNumberValue, setDeviceNumberValue] = useState('');
  const [durationValue, setDurationValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['date'],
    extraParams: [
      'duration',
      'iccid',
      'application',
      'messageTypeRequest',
      'deviceNumber',
    ],
  });

  const {
    date,
    duration,
    iccid,
    application,
    messageTypeRequest,
    deviceNumber,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const filters = useMemo(
    () => ({
      iccid,
      duration,
      application,
      messageTypeRequest,
      deviceNumber,
      start_date: (dateValue ?? moment()).format('DD/MM/YYYY 00:00:00'),
      end_date: (dateValue ?? moment()).format('DD/MM/YYYY 23:59:59'),
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
    }),
    [
      application,
      dateValue,
      deviceNumber,
      duration,
      iccid,
      linesPerPage,
      messageTypeRequest,
      page,
    ],
  );

  const { data } = useSwr('/service-proxy/30445781000199/transaction-history', {
    params: {
      imsi,
      ...filters,
    },
  });

  useEffect(() => {
    if (date) {
      setDateValue(moment(date, 'DDMMYYYY'));
    }
  }, [date]);

  useEffect(() => {
    setDurationValue(duration);
  }, [duration]);

  useEffect(() => {
    setIccidValue(iccid);
  }, [iccid]);

  useEffect(() => {
    setApplicationValue(application);
  }, [application]);

  useEffect(() => {
    setMessageTypeRequestValue(messageTypeRequest);
  }, [messageTypeRequest]);

  useEffect(() => {
    setDeviceNumberValue(deviceNumber);
  }, [deviceNumber]);

  const columns = useMemo(
    () =>
      [
        {
          title: t('table.iccid'),
          dataIndex: 'iccid',
        },
        {
          title: t('table.date'),
          dataIndex: 'date',
          render: dateFormat,
        },
        {
          title: t('table.application'),
          dataIndex: 'application',
        },
        {
          title: t('table.order'),
          dataIndex: 'messageRequestType',
        },
        {
          title: t('table.response'),
          dataIndex: 'messageResponseType',
        },
        {
          title: t('table.duration'),
          dataIndex: 'duration',
          render: (value: string) => `${value} ms`,
        },
        {
          title: t('table.model'),
          dataIndex: 'devicePosModel',
        },
        {
          title: t('table.seller'),
          dataIndex: 'devicePosMerchant',
        },
        {
          title: t('table.logicalNumber'),
          dataIndex: 'devicePosNumber',
        },
        {
          title: t('table.operator'),
          dataIndex: 'operator',
        },
        {
          title: t('table.status'),
          dataIndex: 'status',
        },
      ]?.map(col => ({ ...col, align: 'center' as const })),
    [t],
  );

  const handleDateChange = useCallback(
    (value: Moment | null) => {
      setSearchParams({
        date: value?.format('DDMMYYYY'),
      });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('table.date'),
        element: (
          <AntdDatePicker
            format="DD/MMM/YYYY"
            style={{ width: '100%' }}
            disabledDate={d => d && d > moment().endOf('day')}
            value={dateValue}
            defaultValue={moment()}
            onChange={handleDateChange}
            allowClear={false}
          />
        ),
      },
      {
        visible: true,
        label: t('table.duration'),
        element: (
          <Search
            type="number"
            suffix="ms"
            onSearch={value => setSearchParams({ duration: value })}
            onChange={({ target: { value } }) => setDurationValue(value)}
            value={durationValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.iccid'),
        element: (
          <Search
            onSearch={value => setSearchParams({ iccid: value })}
            onChange={({ target: { value } }) => setIccidValue(value)}
            value={iccidValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.application'),
        element: (
          <Search
            onSearch={value => setSearchParams({ application: value })}
            onChange={({ target: { value } }) => setApplicationValue(value)}
            value={applicationValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.response'),
        element: (
          <Search
            onSearch={value => setSearchParams({ messageTypeRequest: value })}
            onChange={({ target: { value } }) => {
              setMessageTypeRequestValue(value);
            }}
            value={messageTypeRequestValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.logicalNumber'),
        element: (
          <Search
            onSearch={value => setSearchParams({ deviceNumber: value })}
            onChange={({ target: { value } }) => setDeviceNumberValue(value)}
            value={deviceNumberValue}
            allowClear
          />
        ),
      },
    ],
    [
      applicationValue,
      dateValue,
      deviceNumberValue,
      durationValue,
      handleDateChange,
      iccidValue,
      messageTypeRequestValue,
      setSearchParams,
      t,
    ],
  );

  return (
    <Table
      title={t('transactions')}
      columns={columns}
      data={data?.content || []}
      loading={!data?.content}
      tableKey="transactions"
      filters={{
        list: tableFilters,
        openByDefault: true,
        hasValuesInFilters: hasValuesInExtraParams,
        onClearFilters: () => clearSearchParams(),
      }}
      pagination={{ page, linesPerPage, total: data?.totalElements }}
    />
  );
};

export default Transactions;
